import { sanitizeBusinessMessage, toUserFacingApiError } from '../utils/userFacingError'
import { api } from './api'

function assertSuccess(body) {
  if (!body?.success) {
    throw new Error(sanitizeBusinessMessage(body?.message, 'Request failed'))
  }
  return body.data
}

function apiError(err: unknown, fallback: string) {
  return toUserFacingApiError(err, fallback)
}

export type ActivityChannel = 'all' | 'storefront' | 'in_store'

export type TransactionsListParams = {
  page?: number
  limit?: number
  /** Harmonized filter: online order wallet lines vs in-store POS */
  channel?: ActivityChannel
  /** When set, only activity for this store (matches GET /storebusiness/transactions). */
  storeId?: number
}

export type TransactionsListResponse = {
  items: unknown[]
  total: number
  page: number
  limit: number
}

const CHANNEL_LABEL: Record<string, string> = {
  storefront: 'Online store',
  in_store: 'In-store',
  wallet: 'Wallet',
  payout: 'Payout',
}

function listRowCurrency(item: Record<string, unknown>, payload: Record<string, unknown>): string | null {
  const top = item.currency
  if (typeof top === 'string' && top.trim()) return top.trim().toUpperCase()
  const fromPayload = payload.currency
  if (typeof fromPayload === 'string' && fromPayload.trim()) return fromPayload.trim().toUpperCase()
  return null
}

function normalizeActivityRow(item: Record<string, unknown>) {
  const kind = item.kind as string | undefined
  const payload = (item.payload || item) as Record<string, unknown>
  const salesChannel = (payload.salesChannel as string) || ''
  const channelLabel = CHANNEL_LABEL[salesChannel] || salesChannel || '—'
  const detailRef = (payload.detailRef as string) || null
  const currency = listRowCurrency(item, payload)
  const createdAt = item.createdAt

  if (kind === 'ledger') {
    return {
      id: detailRef || `ledger-${payload.id}`,
      listKey: detailRef || `ledger-${payload.id}`,
      rawRef: detailRef || `ledger-${payload.id}`,
      reference: payload.providerReference || String(payload.id),
      orderNumber: (payload.orderReference as string) || payload.orderId || '-',
      amount: payload.amount,
      status: payload.entryType,
      kind: 'ledger',
      salesChannel,
      channelLabel,
      currency,
      createdAt,
    }
  }
  if (kind === 'withdrawal') {
    return {
      id: detailRef || `withdrawal-${payload.id}`,
      listKey: detailRef || `withdrawal-${payload.id}`,
      rawRef: detailRef || `withdrawal-${payload.id}`,
      reference: `withdrawal-${payload.id}`,
      orderNumber: '-',
      amount: payload.amount,
      status: payload.status,
      kind: 'withdrawal',
      salesChannel,
      channelLabel,
      currency,
      createdAt,
    }
  }
  if (kind === 'pos_sale') {
    return {
      id: detailRef || `pos-${payload.id}`,
      listKey: detailRef || `pos-${payload.id}`,
      rawRef: detailRef || `pos-${payload.id}`,
      reference: (payload.saleReference as string) || String(payload.id),
      orderNumber: '-',
      amount: payload.amount,
      status: payload.paymentMethod === 'cash' ? 'Cash' : 'Transfer',
      kind: 'pos_sale',
      salesChannel,
      channelLabel,
      currency,
      createdAt,
    }
  }
  return {
    id: `unknown-${payload.id}`,
    listKey: `unknown-${payload.id}`,
    rawRef: String(payload.id),
    reference: (payload.reference as string) || String(payload.id),
    orderNumber: payload.orderNumber,
    amount: payload.amount,
    status: payload.status,
    kind: 'unknown',
    salesChannel,
    channelLabel: '—',
    currency,
    createdAt,
  }
}

export async function fetchTransactions(
  params: TransactionsListParams = {},
): Promise<TransactionsListResponse> {
  try {
    const page = params.page ?? 1
    const limit = params.limit ?? 20
    const channel = params.channel ?? 'all'
    const { data } = await api.get('/storebusiness/transactions', {
      params: {
        page,
        limit,
        channel,
        ...(params.storeId != null ? { storeId: params.storeId } : {}),
      },
    })
    const payload = assertSuccess(data) as TransactionsListResponse
    const rows = (payload.items || []) as Record<string, unknown>[]
    return {
      items: rows.map((r) => normalizeActivityRow(r)),
      total: payload.total ?? 0,
      page: payload.page ?? page,
      limit: payload.limit ?? limit,
    }
  } catch (e) {
    throw apiError(e, 'Failed to load transactions')
  }
}

export async function fetchTransactionById(ref: string | number) {
  const encoded = encodeURIComponent(String(ref))
  try {
    const { data } = await api.get(`/storebusiness/transactions/${encoded}`)
    const raw = assertSuccess(data) as Record<string, unknown>
    const kind = raw.kind as string | undefined
    if (kind === 'ledger') {
      return {
        id: raw.id,
        detailRef: raw.detailRef,
        reference: raw.providerReference || raw.id,
        orderNumber: raw.orderReference || raw.orderId || '-',
        amount: raw.amount,
        status: raw.entryType,
        kind: 'ledger',
        salesChannel: raw.salesChannel,
        channelLabel: CHANNEL_LABEL[String(raw.salesChannel)] || raw.salesChannel,
        currency: raw.currency,
      }
    }
    if (kind === 'withdrawal') {
      return {
        id: raw.id,
        detailRef: raw.detailRef,
        reference: `withdrawal-${raw.id}`,
        orderNumber: '-',
        amount: raw.amount,
        status: raw.status,
        kind: 'withdrawal',
        salesChannel: raw.salesChannel,
        channelLabel: CHANNEL_LABEL[String(raw.salesChannel)] || raw.salesChannel,
        currency: raw.currency,
      }
    }
    if (kind === 'pos_sale') {
      return {
        id: raw.id,
        detailRef: raw.detailRef,
        reference: raw.saleReference,
        orderNumber: '-',
        amount: raw.total,
        subtotal: raw.subtotal,
        taxTotal: raw.taxTotal,
        status: raw.paymentMethod === 'cash' ? 'Cash' : 'Transfer',
        kind: 'pos_sale',
        salesChannel: raw.salesChannel,
        channelLabel: CHANNEL_LABEL[String(raw.salesChannel)] || raw.salesChannel,
        paymentMethod: raw.paymentMethod,
        staff: raw.staff,
        lines: raw.lines,
        currency: raw.currency,
        createdAt: raw.createdAt,
        store: raw.store,
      }
    }
    return raw
  } catch (e) {
    throw apiError(e, 'Failed to load transaction')
  }
}
